// Sürücü Puantajı — gün gün servis dökümü.
// sortOrder: "asc" (gelecek ajanda) veya "desc" (geçmiş performans).

import React, { useMemo } from "react";

import ServiceMetricsRow from "./ServiceMetricsRow";
import { addDays, formatKm, formatMinutes, formatNumber, shortDate, weekdayName } from "../utils";

const hasServiceIssue = (service) =>
  !!service?.metricsMissing || service?.issueType === "NO_PASSENGERS";

const ISSUE_SORT_RANK = {
  NO_PASSENGERS: 0,
  MISSING_METRICS: 1,
};

const sortDayServices = (services) =>
  [...services].sort((a, b) => {
    const ia = ISSUE_SORT_RANK[a.issueType] ?? 99;
    const ib = ISSUE_SORT_RANK[b.issueType] ?? 99;
    if (ia !== ib) return ia - ib;
    return (a.name || "").localeCompare(b.name || "", "tr");
  });

const DailyBreakdown = ({ days = [], rangeStart, rangeEnd, sortOrder = "desc", emptyDayMessageKey = "driverPerformance.detail.noWorkDay", t }) => {
  const sortedDays = useMemo(() => {
    if (!rangeStart || !rangeEnd) {
      const copy = [...days];
      if (sortOrder === "desc") copy.reverse();
      return copy;
    }

    const byDate = new Map(days.map((d) => [d.date, d]));
    const full = [];
    for (let date = rangeStart; date <= rangeEnd; date = addDays(date, 1)) {
      full.push(
        byDate.get(date) || {
          date,
          count: 0,
          km: 0,
          minutes: 0,
          onDutyMinutes: 0,
          idleMinutes: 0,
          services: [],
        },
      );
    }
    if (sortOrder === "desc") full.reverse();
    return full;
  }, [days, rangeStart, rangeEnd, sortOrder]);

  return (
    <div className="dp-detail__days">
      <h4>{t("driverPerformance.detail.dailyBreakdown")}</h4>
      {sortedDays.map((day) => (
        <DayBlock key={day.date} day={day} emptyDayMessageKey={emptyDayMessageKey} t={t} />
      ))}
    </div>
  );
};

const DayBlock = ({ day, emptyDayMessageKey, t }) => {
  const isEmpty = !day.services?.length;
  const services = useMemo(() => sortDayServices(day.services || []), [day.services]);
  const noPassengersCount = services.filter((s) => s.issueType === "NO_PASSENGERS").length;
  const missingCount = services.filter((s) => s.issueType === "MISSING_METRICS" || s.metricsMissing).length;
  const dayHasIssue = services.some(hasServiceIssue);

  if (isEmpty) {
    return (
      <div className="dp-day dp-day--empty">
        <div className="dp-day__header">
          <span className="dp-day__date">
            <strong>{shortDate(day.date)}</strong> {weekdayName(day.date)}
          </span>
        </div>
        <div className="dp-day__empty-note">{t(emptyDayMessageKey)}</div>
      </div>
    );
  }

  return (
  <div className={`dp-day${dayHasIssue ? " dp-day--has-missing" : ""}`}>
    <div className="dp-day__header">
      <span className="dp-day__date">
        <strong>{shortDate(day.date)}</strong> {weekdayName(day.date)}
      </span>
      <span className="dp-day__stats">
        {formatNumber(day.count)} {t("driverPerformance.servicesShort")} · {formatKm(day.km)} · {formatMinutes(day.minutes)}
        {noPassengersCount > 0 && (
          <span className="dp-tag dp-tag--warn dp-day__missing-tag" title={t("driverScheduler.noPassengersIssueHint")}>
            {noPassengersCount} {t("driverScheduler.noPassengersIssueTag")}
          </span>
        )}
        {missingCount > 0 && (
          <span className="dp-tag dp-tag--warn dp-day__missing-tag" title={t("driverPerformance.metricsMissingHint")}>
            {missingCount} {t("driverPerformance.metricsMissing")}
          </span>
        )}
      </span>
    </div>
    <div className="dp-day__services">
      {services.map((s, i) => (
        <ServiceMetricsRow key={`${s.serviceId}-${i}`} service={s} t={t} />
      ))}
    </div>
  </div>
  );
};

export default DailyBreakdown;
